import { useCallback, useMemo, useState } from "react"
import { DateTime } from "luxon"
import { makeStyles } from "tss-react/mui"
import { Grid, Typography } from "@mui/material"
import { faShieldCheck } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MRT_ColumnDef } from "material-react-table"
import { SUPPORT_ADMIN_ROUTE } from "../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { DebtorsInvoicingBasisResponse, SupportInvoicingQuery, fetchDebtorsInvoicingBasis } from "../adminInvoicingApi"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { DebtorsDetailsContent } from "../components/DebtorsDetailsContent"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { Debtor, DebtorDetails } from "../adminInvoicingTypes"
import { DebtorSelector } from "../components/DebtorSelector"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"

const useStyles = makeStyles()((theme) => ({
    dateFilter: {
        width: "11rem",
    },
    tableContainer: {
        maxHeight: "100%",
        padding: "1.5rem",
    },
    tableRow: {
        background: theme.palette.tailwind.slate[50],
    },
    tableHeadRow: {
        display: "none",
    },
    headCellRowExpand: {
        padding: "0",
        border: "none",
    },
    bodyCellRowExpand: {
        padding: "0",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRight: "none",
        // borderTopLeftRadius: "0.75rem",
        // borderBottomLeftRadius: "0.75rem",
    },
    tableBodyCell: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderLeft: "none",
        padding: "1.25rem",
        paddingLeft: 0,
        // borderTopRightRadius: "0.75rem",
        // borderBottomRightRadius: "0.75rem",
    },
    tableDetailPanel: {
        borderLeft: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRight: `1px solid ${theme.palette.tailwind.slate[200]}`,
        // borderBottomRightRadius: "0.75rem",
        // borderBottomLeftRadius: "0.75rem",
        background: "white",
        padding: 0,
    },
    tableTopActions: {
        paddingTop: "1rem",
        paddingBottom: "1rem",
        marginLeft: "1rem",
        gap: "2%",
    },
    title: {
        fontSize: ".875rem",
        fontWeight: 700,
        color: "black",
        "& > span": {
            fontWeight: 400,
            display: "block",
        },
    },
}))

const selectDebtorsInvoicingBasis = (data: DebtorsInvoicingBasisResponse) => data?.debitors || []

function BasisForInvoicingTable() {
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate,
        endDate,
    })
    const [debtorId, setDebtorId] = useState<Debtor | null>(null)

    const debtorsInvoicingBasisResult = useApiQuery(fetchDebtorsInvoicingBasis, {
        queryName: SupportInvoicingQuery.DEBTORS_INVOICING_BASIS,
        dependencies: {
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
            ...(debtorId ? { debtorId: debtorId.id } : {}),
        },
        select: selectDebtorsInvoicingBasis,
    })

    const { ApiQueryTable } = useApiQueryTable(debtorsInvoicingBasisResult)

    const columns = useMemo<MRT_ColumnDef<DebtorDetails>[]>(
        () => [
            {
                header: "",
                accessorKey: "name",
                Cell: ({ row }) => (
                    <Typography variant="subtitle1" className={classes.title}>
                        {row.original.debitorNo}, {row.original.name}
                        <span>{row.original.address}</span>
                    </Typography>
                ),
                size: 900,
            },
        ],
        [l10n],
    )

    const handleChangeDebtor = useCallback(
        (el: Debtor | null) => {
            setDebtorId(el)
        },
        [setDebtorId],
    )

    return (
        <Grid container direction="column">
            <ApiQueryTable
                enableExpanding
                enableExpandAll={false}
                columns={columns}
                enableClickToCopy={false}
                enableColumnOrdering
                enableStickyHeader
                enableDensityToggle={false}
                initialState={{ density: "spacious" }}
                enableHiding={false}
                enableRowActions={false}
                enableGrouping
                enableColumnDragging={false}
                enableBottomToolbar={false}
                muiTableContainerProps={{ className: classes.tableContainer }}
                muiTableBodyRowProps={{ className: classes.tableRow }}
                muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                muiTableBodyCellProps={{ className: classes.tableBodyCell }}
                muiTableDetailPanelProps={{ className: classes.tableDetailPanel }}
                renderDetailPanel={({ row }) => <DebtorsDetailsContent info={row.original} />}
                displayColumnDefOptions={{
                    "mrt-row-expand": {
                        header: "",
                        muiTableHeadCellProps: { className: classes.headCellRowExpand },
                        muiTableBodyCellProps: { align: "center", className: classes.bodyCellRowExpand },
                    },
                }}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                        <DebtorSelector debtorId={debtorId} handleChange={handleChangeDebtor} />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
            />
        </Grid>
    )
}

export function SupportBasisForInvoicing() {
    return (
        <>
            <LocalizedStrict id="support-reports-basis-for-invoicing-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_ADMIN_INVOICING_BASIS_FOR_INVOICING}
                    title="Basis for invoicing"
                    icon={<FontAwesomeIcon icon={faShieldCheck} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-reports-invoicing-header-nav-item">
                            <Breadcrumb>Invoicing</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-reports-invoicing-header-nav-item-basis-for-invoicing">
                            <Breadcrumb>Basis for invoicing</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <BasisForInvoicingTable />
        </>
    )
}
